import { ErrorMessage } from '@hookform/error-message'
import { Modal, Col } from 'antd'
import { FC, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import PerButton from '../../../../../components/custom_components/Button'
import PerButtonCircle from '../../../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../../../components/custom_components/FlexRow'
import PerInputNumber from '../../../../../components/custom_components/PerInputNumber'
import PerSpacer from '../../../../../components/custom_components/Spacer'
import PerText from '../../../../../components/custom_components/Text'
import { useAuth } from '../../../../../context/auth-context'
import { useCurrency } from '../../../../../context/currency-context'
import { useAmountAndCurrency } from '../../../../../shared/useAmountAndCurrency'
import { LatestContractDtoFragment, FullMeetingFragment } from '../../../../../types'
import { EditBonusAmountForm, useEditBonusAmountModalStore } from './EditBonusAmountModalStore'
import { editFlexibleBonusByEmployeeId } from './useBonusAmountEditableIsEnabled'
import { ProrataTooltip } from '../ProrataTooltip'
import { ProrataPartial } from '../targetBonusCalculation-util'

export interface EditBonusAmountModalProps {
  currentContract: LatestContractDtoFragment
  bonusAmount?: number
  annualAmount: number
  prorataAmount: number
  prorataPartials: ProrataPartial[]
  employeeTimezone: string
  meeting: FullMeetingFragment
}

export const EditBonusAmountModal: FC<EditBonusAmountModalProps> = ({
  currentContract,
  bonusAmount,
  annualAmount,
  prorataAmount,
  prorataPartials,
  employeeTimezone,
  meeting,
}) => {
  const isModalOpened = useEditBonusAmountModalStore((state) => state.isModalOpened)

  return (
    <Modal
      centered
      title={<EditBonusAmountModalHeader />}
      visible={isModalOpened}
      closable={false}
      footer={<EditBonusAmountModalFooter meeting={meeting} />}
      destroyOnClose={true}
      className="per-modal"
    >
      <EditBonusAmountModalContent
        currentContract={currentContract}
        bonusAmount={bonusAmount}
        prorataAmount={prorataAmount}
        prorataPartials={prorataPartials}
        annualAmount={annualAmount}
        employeeTimezone={employeeTimezone}
        meeting={meeting}
      />
    </Modal>
  )
}

export const EditBonusAmountModalHeader = (): JSX.Element => {
  const { t } = useTranslation()
  const [closeModal, cleanModal] = useEditBonusAmountModalStore((state) => [state.closeBonusModal, state.cleanBonusModal], shallow)

  const handleClose = () => {
    closeModal()
    cleanModal()
  }

  return (
    <PerFlexRow justify="space-between" align="middle">
      <PerFlexRow>
        <PerText fontSize="xl" color="primary">
          {t('appraisalMeeting.targetBonusOverview.editBonusAmount')}
        </PerText>
      </PerFlexRow>
      <PerButtonCircle size="large" icon="close_stroke" color="transparent" onClick={handleClose} testid="tm-modal-close" />
    </PerFlexRow>
  )
}

export const EditBonusAmountModalFooter: FC<{ meeting: FullMeetingFragment }> = ({ meeting }) => {
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [closeModal, cleanModal, editBonusAmountForm] = useEditBonusAmountModalStore(
    (state) => [state.closeBonusModal, state.cleanBonusModal, state.editBonusAmountForm],
    shallow,
  )

  const handleClose = () => {
    closeModal()
    cleanModal()
  }

  const handleSubmit = async () => {
    if (!!editBonusAmountForm && !!editBonusAmountForm.trigger) {
      await editBonusAmountForm.trigger()
      if (Object.keys(editBonusAmountForm.formState.errors).length) {
        setErrorMessage(t('formError.editBonusAmountModal.errorForm'))
      } else {
        const values = editBonusAmountForm.getValues()
        const { errors } = await editFlexibleBonusByEmployeeId(values.bonusAmountField, meeting.employee.id, meeting.id)
        if (!errors) {
          handleClose()
        }
      }
    }
  }

  return (
    <PerFlexRow align="middle" justify="space-between">
      <PerFlexRow>
        <PerButton
          text={t('global.button.cancel')}
          color="secondary"
          before="close_stroke"
          variant="pastel"
          type="button"
          onClick={handleClose}
        />
      </PerFlexRow>
      <PerFlexRow align="middle">
        {!!errorMessage && (
          <PerText appearance="info" color="error">
            {errorMessage}
          </PerText>
        )}
        <PerSpacer ml="xxxs" mr="xxxs" />
        <PerButton
          text={t('global.button.save')}
          color="success"
          before="check_stroke"
          variant="pastel"
          type="button"
          onClick={handleSubmit}
        />
      </PerFlexRow>
    </PerFlexRow>
  )
}

export const EditBonusAmountModalContent: FC<EditBonusAmountModalProps> = ({
  bonusAmount,
  annualAmount,
  prorataAmount,
  prorataPartials,
  employeeTimezone,
  meeting,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatCurrency } = useAmountAndCurrency(user.locale)
  const { currency } = useCurrency()
  const [setEditBonusModalForm] = useEditBonusAmountModalStore((state) => [state.setEditBonusModalForm], shallow)
  const year = meeting.targetsCalculationHelper.newTargetsReferenceYear

  const editBonusModalForm = useForm<EditBonusAmountForm>({
    defaultValues: { bonusAmountField: !!bonusAmount ? bonusAmount : prorataAmount },
    criteriaMode: 'all',
    mode: 'onChange',
  })

  const {
    control,
    formState: { errors },
    trigger,
  } = editBonusModalForm

  useEffect(() => {
    setEditBonusModalForm(editBonusModalForm)
    trigger()
  }, [setEditBonusModalForm, editBonusModalForm, trigger])

  return (
    <>
      <PerText wordBreak={false} color="secondary" fontSize="s" mb="s">
        {t('appraisalMeeting.targetBonusOverview.bonusAmountDescription')}
      </PerText>

      <PerText wordBreak={false} color="error" fontSize="s" mb="s">
        {t('appraisalMeeting.targetBonusOverview.bonusAmountNote')}
      </PerText>

      <PerFlexRow direction="column" width="60%">
        <PerText color="secondary" fontWeight="bold" fontSize="xs" mb="xxs">
          {t('appraisalMeeting.targetBonusOverview.bonusAmount', { year })}
        </PerText>

        <Controller
          name="bonusAmountField"
          control={control}
          rules={{
            required: `${t('formError.editBonusAmountModal.errorField')}`,
            max: { value: annualAmount, message: `${t('formError.editBonusAmountModal.errorField')}` },
            min: { value: prorataAmount, message: `${t('formError.editBonusAmountModal.errorField')}` },
          }}
          render={({ field: { onChange, value } }) => (
            <>
              <div>
                <PerInputNumber
                  type="currency"
                  value={value}
                  onChange={onChange}
                  width={'45%'}
                  currency={currency}
                  placeholder="0"
                  min={0}
                  timezoneAndLocale={user}
                />
              </div>

              {errors && errors['bonusAmountField'] && (
                <>
                  <PerSpacer mb="xxxs" />
                  <ErrorMessage
                    errors={errors}
                    name="bonusAmountField"
                    render={({ messages }) => {
                      return (
                        <>
                          <PerText fontSize="xs" color="error">
                            {!!messages && !!messages.required && messages.required}
                          </PerText>
                          <PerText fontSize="xs" color="error">
                            {!!messages && !!messages.max && messages.max}
                          </PerText>
                          <PerText fontSize="xs" color="error">
                            {!!messages && !!messages.min && messages.min}
                          </PerText>
                        </>
                      )
                    }}
                  />
                </>
              )}
            </>
          )}
        />
      </PerFlexRow>

      <PerFlexRow direction="row" mt="s">
        <Col span={9}>
          <PerFlexRow direction="column">
            <PerText color="secondary" fontWeight="bold" fontSize="xs" mb="xxs">
              {t('appraisalMeeting.targetBonusOverview.annualAmount')}
            </PerText>
            <PerText>{formatCurrency(annualAmount, currency, true)}</PerText>
          </PerFlexRow>
        </Col>

        <Col span={15}>
          <PerFlexRow direction="column">
            <PerText color="secondary" fontWeight="bold" fontSize="xs" mb="xxs">
              {t('appraisalMeeting.targetBonusOverview.proratedAmount')}
            </PerText>
            <PerText mb="xxxs">{formatCurrency(prorataAmount, currency, true)}</PerText>
            <ProrataTooltip
              prorataPartials={prorataPartials}
              displayOnlyText={true}
              currency={currency}
              employeeTimezone={employeeTimezone}
            />
          </PerFlexRow>
        </Col>
      </PerFlexRow>
    </>
  )
}
